import {
  FileDown,
  FileUp,
  FilePlus,
  FolderOpen,
  Printer,
  Redo2,
  Save,
  Undo2,
  ZoomIn,
  ZoomOut,
  Grid3X3,
  Settings,
  HelpCircle,
  Trash2,
  Info,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useElementsStore } from "@/stores/elementsStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { useLabelStore } from "@/stores/labelStore";
import logoImg from "@/logo.svg";
import { APP_VERSION } from "@/components/dialogs/AboutDialog";

interface HeaderProps {
  onNewLabel: () => void;
  onSave: () => void;
  onLoad: () => void;
  onImportZPL: () => void;
  onExportZPL: () => void;
  onPrint: () => void;
  onSettings: () => void;
  onDiscard: () => void;
  onAbout: () => void;
  isPrinting?: boolean;
}

export function Header({
  onNewLabel,
  onSave,
  onLoad,
  onImportZPL,
  onExportZPL,
  onPrint,
  onSettings,
  onDiscard,
  onAbout,
  isPrinting = false,
}: HeaderProps) {
  const { undo, redo, canUndo, canRedo, elements } = useElementsStore();
  const { zoom, setZoom, showGrid, setShowGrid } = useSettingsStore();
  const { label } = useLabelStore();

  const handleZoomIn = () => setZoom(Math.min(zoom + 0.25, 3));
  const handleZoomOut = () => setZoom(Math.max(zoom - 0.25, 0.25));

  const handleOpenManual = () => {
    // Check if running in Electron
    if (window.electronAPI?.openManual) {
      window.electronAPI.openManual();
    } else {
      window.open("/manual.html?v=1", "_blank");
    }
  };

  return (
    <header className="h-14 border-b bg-background flex items-center px-4 gap-2">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 mr-4">
          <img src={logoImg} alt="NEO" className="h-8" />
          <div className="flex flex-col ml-2">
            <span className="font-semibold text-sm leading-tight">
              ZPL Designer
            </span>
            <span className="text-[10px] text-muted-foreground/60 leading-tight">
              v{APP_VERSION}
            </span>
          </div>
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={onNewLabel}>
              <FilePlus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>New Label</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={onLoad}>
              <FolderOpen className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Open</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={onSave}>
              <Save className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Save</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDiscard}
              disabled={elements.length === 0}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Discard Label</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-6" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={undo}
              disabled={!canUndo()}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Undo</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={redo}
              disabled={!canRedo()}
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Redo</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-6" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleZoomOut}>
              <ZoomOut className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Zoom Out</TooltipContent>
        </Tooltip>

        <span className="text-sm w-16 text-center">
          {Math.round(zoom * 100)}%
        </span>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleZoomIn}>
              <ZoomIn className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Zoom In</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-6" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={showGrid ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setShowGrid(!showGrid)}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Toggle Grid</TooltipContent>
        </Tooltip>
      </div>

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {label.name} ({label.width}×{label.height}mm @ {label.dpi} DPI)
        </span>

        <Separator orientation="vertical" className="h-6" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={onSettings}>
              <Settings className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Settings</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleOpenManual}>
              <HelpCircle className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Help / Manual</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={onAbout}>
              <Info className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>About</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" onClick={onImportZPL}>
              <FileUp className="h-4 w-4 mr-2" />
              Import ZPL
            </Button>
          </TooltipTrigger>
          <TooltipContent>Import ZPL Code</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" onClick={onExportZPL}>
              <FileDown className="h-4 w-4 mr-2" />
              Export ZPL
            </Button>
          </TooltipTrigger>
          <TooltipContent>Export ZPL Code</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" onClick={onPrint} disabled={isPrinting}>
              {isPrinting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Printer className="h-4 w-4 mr-2" />
              )}
              {isPrinting ? 'Printing...' : 'Print'}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Send to Printer</TooltipContent>
        </Tooltip>
      </div>
    </header>
  );
}
